import React, { useEffect, useState, useRef, useMemo } from "react";
import moment from "moment-timezone";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import { toastMessage } from "../../utils/toastMessage";
import { getLeaveAMessageThreadsApi, getLeaveAMessageMessagesApi } from "../../services/userManagement";
import { dateFormatWithTime } from "../../utils/dateFormat";
import NotFound from "../../components/not-found/NotFound";
import common from "../../routes/common";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const { NOT_FOUND } = common;

const getDateKey = (dateStr) => {
  if (!dateStr) return "";
  const m = moment(dateStr);
  return m.format("YYYY-MM-DD");
};
const formatDateLabel = (dateKey) => {
  const m = moment(dateKey);
  const today = moment().format("YYYY-MM-DD");
  const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
  if (dateKey === today) return "Today";
  if (dateKey === yesterday) return "Yesterday";
  return m.format("DD MMM YYYY");
};

const UserSupportListing = ({ userId, username }) => {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [messagesPage, setMessagesPage] = useState(1);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const messagesEndRef = useRef(null);
  const messagesLimit = 50;

  const dateList = useMemo(() => {
    const set = new Set();
    messages.forEach((msg) => {
      const key = getDateKey(msg.createdAt);
      if (key) set.add(key);
    });
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [messages]);

  const messagesByDate = useMemo(() => {
    if (!selectedDateKey) return messages;
    return messages.filter((msg) => getDateKey(msg.createdAt) === selectedDateKey);
  }, [messages, selectedDateKey]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  useEffect(() => {
    if (messagesByDate.length) scrollToBottom();
  }, [messagesByDate.length, selectedThread?.thread_id, selectedDateKey]);

  const fetchThreads = async () => {
    if (!userId) return;
    try {
      showLoader();
      const { data } = await getLeaveAMessageThreadsApi({ userId });
      if (data.code === 200) {
        setThreads(data?.result?.list || []);
        if (!selectedThread && (data?.result?.list || []).length > 0) {
          setSelectedThread((data.result.list)[0]);
        }
      } else {
        setThreads([]);
        toastMessage(data.message || "Failed to load threads", "error", "supportThreads");
      }
    } catch (error) {
      console.error("error", error);
      setThreads([]);
      toastMessage("Failed to load threads", "error", "supportThreads");
    } finally {
      hideLoader();
    }
  };

  const fetchMessages = async (threadId, page = 1) => {
    if (!threadId) return;
    try {
      if (page === 1) showLoader();
      const { data } = await getLeaveAMessageMessagesApi({
        threadId,
        page,
        limit: messagesLimit
      });
      if (data.code === 200) {
        const list = data?.result?.messages || [];
        if (page === 1) {
          setMessages([...list].reverse());
          setMessagesPage(1);
        } else {
          setMessages(prev => [...(list || []).reverse(), ...prev]);
        }
        setMessagesTotal(data?.result?.total ?? 0);
      } else {
        if (page === 1) setMessages([]);
        toastMessage(data.message || "Failed to load messages", "error", "supportMessages");
      }
    } catch (error) {
      console.error("error", error);
      if (page === 1) setMessages([]);
      toastMessage("Failed to load messages", "error", "supportMessages");
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchThreads();
  }, [userId]);

  useEffect(() => {
    if (selectedThread?.thread_id) {
      setSelectedDateKey(null);
      fetchMessages(selectedThread.thread_id, 1);
    } else {
      setMessages([]);
      setMessagesTotal(0);
      setSelectedDateKey(null);
    }
  }, [selectedThread?.thread_id]);

  const loadMoreMessages = () => {
    if (!selectedThread?.thread_id || messages.length >= messagesTotal) return;
    const nextPage = messagesPage + 1;
    setMessagesPage(nextPage);
    fetchMessages(selectedThread.thread_id, nextPage);
  };

  const isFromUser = (msg) => {
    const from = msg?.from;
    if (!from) return false;
    const id = (typeof from === "object" && from !== null) ? (from._id || from.id) : null;
    return id && userId && id.toString() === userId.toString();
  };

  return (
    <>
      {loader}
      <div className="row">
        {/* <div className="col-12">
          <h5 className="mb-2" style={{ color: "#333", fontWeight: 600 }}>
            Customer Consultant User Support
          </h5>
          <p className="text-muted small mb-0">
            Select a conversation, then pick a date to view chat for that day.
          </p>
        </div> */}
      </div>

      {threads.length === 0 ? (
        <NotFound msg="No leave-a-message threads found for this user." />
      ) : (
        <div
          className="mt-3 w-100"
          style={{
            minHeight: "calc(100vh - 280px)",
            maxHeight: "calc(100vh - 220px)",
            border: "1px solid #e0e0e0",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "row"
          }}
        >
          {/* Left sidebar: Conversations + Date list - fixed width like WhatsApp */}
          <div
            style={{
              width: "320px",
              minWidth: "280px",
              flexShrink: 0,
              borderRight: "1px solid #e0e0e0",
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden"
            }}
          >
            <div className="p-2 border-bottom" style={{ background: "#f0f2f5" }}>
              <span className="small text-muted d-flex align-items-center gap-1">
                <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} /> Conversations
              </span>
            </div>
            <div style={{ overflowY: "auto", flex: "1 1 0" }}>
              {threads.map((t) => {
                const isSelected = selectedThread?.thread_id === t.thread_id;
                return (
                  <div
                    key={t.thread_id}
                    onClick={() => setSelectedThread(t)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && setSelectedThread(t)}
                    style={{
                      padding: "12px 14px",
                      cursor: "pointer",
                      borderBottom: "1px solid #eee",
                      background: isSelected ? "#ffebeb" : "transparent",
                      borderLeft: isSelected ? "3px solid #ff8989" : "3px solid transparent"
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background: isSelected ? "#ff8989" : "#e0e0e0",
                          color: isSelected ? "#fff" : "#666",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0
                        }}
                      >
                        <PersonOutlineIcon sx={{ fontSize: 22 }} />
                      </div>
                      <div className="flex-grow-1 min-w-0">
                        <div className="fw-semibold text-truncate" style={{ fontSize: "14px" }}>
                          {t.consultantName || "Consultant"}
                        </div>
                        <div className="small text-muted">
                          {t.updatedAt ? dateFormatWithTime(t.updatedAt) : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Date list - show when thread selected */}
            {selectedThread && (
              <>
                <div className="p-2 border-top border-bottom" style={{ background: "#f0f2f5" }}>
                  <span className="small text-muted d-flex align-items-center gap-1">
                    <CalendarTodayIcon sx={{ fontSize: 18 }} /> Dates
                  </span>
                </div>
                <div style={{ overflowY: "auto", maxHeight: "220px" }}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedDateKey(null)}
                    onKeyDown={(e) => e.key === "Enter" && setSelectedDateKey(null)}
                    style={{
                      padding: "10px 14px",
                      cursor: "pointer",
                      borderBottom: "1px solid #eee",
                      background: selectedDateKey === null ? "#ffebeb" : "transparent",
                      borderLeft: selectedDateKey === null ? "3px solid #ff8989" : "3px solid transparent"
                    }}
                  >
                    <span className="small fw-medium">All dates</span>
                  </div>
                  {dateList.map((dateKey) => {
                    const isDateSelected = selectedDateKey === dateKey;
                    return (
                      <div
                        key={dateKey}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedDateKey(dateKey)}
                        onKeyDown={(e) => e.key === "Enter" && setSelectedDateKey(dateKey)}
                        style={{
                          padding: "10px 14px",
                          cursor: "pointer",
                          borderBottom: "1px solid #eee",
                          background: isDateSelected ? "#ffebeb" : "transparent",
                          borderLeft: isDateSelected ? "3px solid #ff8989" : "3px solid transparent"
                        }}
                      >
                        <span className="small">{formatDateLabel(dateKey)}</span>
                      </div>
                    );
                  })}
                  {dateList.length === 0 && messages.length > 0 && (
                    <div className="p-2 small text-muted">No dates</div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Chat area - takes remaining width */}
          <div
            className="d-flex flex-column flex-grow-1"
            style={{ minWidth: 0, background: "#f5f5f5" }}
          >
            {selectedThread ? (
              <>
                <div
                  className="px-3 py-2 border-bottom d-flex align-items-center gap-2"
                  style={{ background: "#fff" }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: "#ff8989",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <PersonOutlineIcon sx={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <div className="fw-semibold" style={{ fontSize: "15px" }}>
                      {selectedThread.consultantName || "Consultant"}
                    </div>
                    {/* <div className="small text-muted">
                      {selectedDateKey ? `Chat on ${formatDateLabel(selectedDateKey)}` : "Leave a message thread"}
                    </div> */}
                  </div>
                </div>

                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px"
                  }}
                >
                  {messages.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      No messages yet in this thread.
                    </div>
                  ) : messagesByDate.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      No messages on this date.
                    </div>
                  ) : (
                    <>
                      {!selectedDateKey && messages.length < messagesTotal && (
                        <div className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={loadMoreMessages}
                          >
                            Load older messages
                          </button>
                        </div>
                      )}
                      {messagesByDate.map((msg) => {
                        const fromUser = isFromUser(msg);
                        return (
                          <div
                            key={msg._id || msg.id || Math.random()}
                            style={{
                              display: "flex",
                              justifyContent: fromUser ? "flex-start" : "flex-end"
                            }}
                          >
                            <div
                            style={{
                              maxWidth: "70%",
                              padding: "10px 14px",
                              borderRadius: fromUser
                                ? "16px 16px 16px 4px"
                                : "16px 16px 4px 16px",
                              background: fromUser ? "#fff" : "#ff8989",
                              color: fromUser ? "#1a1a1a" : "#fff",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.08)"
                            }}
                            >
                              <div
                                className="small mb-1"
                                style={{
                                  opacity: 0.9,
                                  fontWeight: 600,
                                  fontSize: "11px"
                                }}
                              >
                                {fromUser ? (username || "User") : (selectedThread?.consultantName || "Consultant")}
                              </div>
                              <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {msg.content || "—"}
                              </div>
                              <div
                                className="small mt-1"
                                style={{ opacity: 0.8, fontSize: "11px" }}
                              >
                                {msg.createdAt ? dateFormatWithTime(msg.createdAt) : ""}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>
              </>
            ) : (
              <div
                className="d-flex flex-column align-items-center justify-content-center text-muted py-5 px-3 flex-grow-1"
              >
                <ChatBubbleOutlineIcon sx={{ fontSize: 48, opacity: 0.4 }} />
                <p className="mt-2 mb-0">Select a conversation to view chat history</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default UserSupportListing;
